import React, { useMemo } from 'react';
import useMarketShareHooks from 'src/components/AtlasV2Components/Hooks/useMarketShareRequestHook';
import { useAtlasV2MetricByWeekId } from 'src/serverProxy/useAtlasV2MetricByWeekId';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import { SplineChartV2 } from 'src/components/BeaconV2Components/SplineChartV2';
import type { SplineChartV2Series } from 'src/components/BeaconV2Components/SplineChartV2';
import { getDirection } from 'src/components/BeaconV2Components/utils/chartStyles';
import { useAppSelector } from 'src/utils/Hooks';
import { timestampToWeekId } from 'src/utils/dateUtils';
import SplineChartLoading from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import NoDataPlaceHolder from 'src/components/BeaconV2Components/common/NoDataPlaceHolder/NoDataPlaceHolder';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';
import { calculatePercentChange } from 'src/utils/app';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import { useBrandEntity, useCompanyName, useMainEntityName, useMainEntityType } from 'src/utils/Hooks/reduxSelectors';
import { useMainTimePeriodShortDisplayName, useComparisonTimePeriodShortDisplayName } from 'src/utils/Hooks/typedHooks';

const TOPIC_ENTITY_TYPES = ['searchtermlist', 'topic', 'topicgroup'];

export const stripSurroundingQuotes = (str: string): string => {
  if (
    str &&
    str.length >= 2 &&
    ((str[0] === "'" && str[str.length - 1] === "'") || (str[0] === '"' && str[str.length - 1] === '"'))
  ) {
    return str.slice(1, -1);
  }
  return str;
};

const toSplineChartSeries = (data: number[][], id: string, name: string): SplineChartV2Series => ({
  id,
  name,
  data: data.map(([ts, val]) => ({ weekId: timestampToWeekId(ts), value: val ?? null }))
});

export interface AtlasV2TrafficShareSplineChartProps {
  /** ES field name, e.g. 'organicClicks' or 'adClicks' */
  fieldName: string;
  /** Chart title, e.g. 'Share of Organic Traffic' */
  title: string;
  /** Label used in the subtitle sentence, e.g. 'organic traffic' or 'paid traffic' */
  trafficLabel: string;
}

/**
 * Shared market-share spline chart for traffic subtabs.
 * Uses ElasticSearch via useAtlasV2MetricByWeekId with marketShareBuilder
 * to compute client share / total market share per week as a percentage.
 */
const AtlasV2TrafficShareSplineChart = ({ fieldName, title, trafficLabel }: AtlasV2TrafficShareSplineChartProps) => {
  const { requestBuilder, marketShareBuilder } = useMarketShareHooks();

  const metricData = useAtlasV2MetricByWeekId({
    indexName: 'traffic',
    fieldName,
    requestBuilder,
    marketShareBuilder,
    metricTypeDisplay: METRICTYPE.PERCENT
  });

  const {
    primarySlicedData,
    secondarySlicedData,
    formattedPrimaryTotal,
    formattedSecondaryTotal,
    formattedPercentChange,
    primaryTotal,
    secondaryTotal,
    percentChange,
    isLoading
  } = metricData;

  const primaryLegendDisplayName = useAppSelector((state) => state.mainTimePeriod.displayName);
  const comparisonLegendDisplayName = useAppSelector((state) => state.comparisonTimePeriod.displayName);

  const companyName = useCompanyName();
  const entityName = useMainEntityName();
  const taxonomyText = useTaxonomySubtitleText();
  const mainTimePeriodShortDisplayName = useMainTimePeriodShortDisplayName();
  const comparisonTimePeriodShortDisplayName = useComparisonTimePeriodShortDisplayName();
  const entityPhrase = useEntityPhrasing();
  const mainEntityType = useMainEntityType();
  const { isBrandEntity } = useBrandEntity();
  const isTopicEntity = TOPIC_ENTITY_TYPES.includes(mainEntityType);
  const topicDisplayName = isTopicEntity ? stripSurroundingQuotes(entityName || '') : '';

  const primarySeries = useMemo(
    () => toSplineChartSeries(primarySlicedData || [], `traffic-${fieldName}-share-primary`, title),
    [primarySlicedData, fieldName, title]
  );

  const comparisonSeries = useMemo(
    () =>
      toSplineChartSeries(secondarySlicedData || [], `traffic-${fieldName}-share-comparison`, `${title} (Comparison)`),
    [secondarySlicedData, fieldName, title]
  );

  const pctChange = calculatePercentChange(primaryTotal, secondaryTotal);
  const percentDirection = pctChange >= 0 ? 'higher' : 'lower';

  const subjectName = isTopicEntity ? topicDisplayName : isBrandEntity ? entityPhrase : companyName;
  const locationPhrase = isTopicEntity
    ? `in the ${topicDisplayName} topic `
    : !['company', 'brand'].includes(mainEntityType)
      ? `in ${entityPhrase} `
      : '';

  const subtitle = (
    <BeaconSubtitleContainer>
      <Text variant="body1" lineHeight="24px">
        {`${subjectName} earned ${formattedPrimaryTotal} share of ${trafficLabel} ${locationPhrase}`}
        {!isTopicEntity && taxonomyText}
        {` during the ${mainTimePeriodShortDisplayName} period, which is ${formattedPercentChange} ${percentDirection} compared to the ${comparisonTimePeriodShortDisplayName}.`}
        <SubtitleFilters showFilterNumber={false} />
      </Text>
    </BeaconSubtitleContainer>
  );

  return (
    <BeaconChartWithLegend
      title={title}
      primaryLegendProps={{
        displayName: primaryLegendDisplayName,
        metric: formattedPrimaryTotal,
        change: formattedPercentChange,
        direction: getDirection(percentChange)
      }}
      comparisonLegendProps={{
        displayName: comparisonLegendDisplayName,
        metric: formattedSecondaryTotal,
        direction: getDirection(percentChange)
      }}
      loading={isLoading}
      loadingComponent={<SplineChartLoading includeSubtitle />}
      includeSummaryInfo={false}
      subtitle={subtitle}
    >
      <SplineChartV2
        primarySeries={primarySeries}
        comparisonSeries={comparisonSeries}
        metricType={METRICTYPE.PERCENT}
        NoDataPlaceholder={
          <NoDataPlaceHolder
            title="Data currently unavailable"
            description="There is currently no data matching your selected criteria. Please try another query to view insights."
            flexProps={{ margin: '80px auto' }}
          />
        }
      />
    </BeaconChartWithLegend>
  );
};

export default AtlasV2TrafficShareSplineChart;
